import booleanContains from '@turf/boolean-contains'
import type { FeatureCollection } from 'geojson'
import { useMemo } from 'react'
import { Layer, LayerProps, Source } from 'react-map-gl'
import { aDrawnFeature, aSelectedListing, useAtomValue } from '~/atoms'
import { MapView, useCurrentMapView } from '~/hooks'
import { Listing } from '~/models'
import { CLUSTER_ZOOM_LEVEL, fmtAddress, PROPERTY_TYPES_COLORS } from '~/utils'

export default function MapPropertyClustersLayer({
  properties,
}: {
  properties: Listing[]
}) {
  const currentMapView = useCurrentMapView()
  const selectedListing = useAtomValue(aSelectedListing)
  const drawnFeature = useAtomValue(aDrawnFeature)

  const selectedListingPoint = useMemo(() => {
    if (!selectedListing) {
      return undefined
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            ...selectedListing.location,
            type: 'Point',
          },
          properties: {
            ...selectedListing,
          },
        },
      ],
    } as FeatureCollection
  }, [selectedListing])

  const points: FeatureCollection = useMemo(() => {
    let features = properties.filter((l: any) => l.location)
    if (drawnFeature) {
      features = features.filter((l: any) =>
        booleanContains(drawnFeature, l.location)
      )
    }

    return {
      type: 'FeatureCollection',
      features: features.map((l: any) => {
        const address = l.isBusinessListing
          ? fmtAddress(l.property, { locationVisibility: l.locationVisibility })
          : [l.property?.address, l.property?.address2]
              .filter(Boolean)
              .join(' ')
        return {
          type: 'Feature',
          geometry: {
            ...l.location,
            type: 'Point',
          },
          properties: {
            _id: l._id,
            slug: l.slug,
            status: l.status,
            address: address,
            type: l.type,
            imageType: l.type === 'saleLease' ? 'sale-lease' : l.type,
            name: l.name,
            lng: l.location.coordinates[0],
            lat: l.location.coordinates[1],
            primaryPropertyType:
              l.propertyTypes.length > 0 ? l.propertyTypes[0] : 'Other',
            propertyTypes: l.propertyTypes.join(', '),
            isBusinessListing: l.isBusinessListing || false,
          },
        }
      }),
    }
  }, [properties, drawnFeature])

  const clusterCountLayer: LayerProps = {
    id: 'property-clusters-count',
    type: 'symbol',
    source: 'property-listings',
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['Roboto Medium'],
      'text-size': 12,
      'text-offset': [0, 0.15],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': '#ffffff',
    },
  }

  const clusterLayer: LayerProps = {
    type: 'circle',
    id: 'property-clusters',
    source: 'property-listings',
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': '#7c3aed',
      'circle-radius': 18,
      'circle-stroke-color': '#7c3aed',
      'circle-stroke-opacity': 0.25,
      'circle-stroke-width': 5,
    },
  }

  const layerCaseStyles = useMemo(() => {
    const cases: any = []
    const types = ['lease', 'sale', 'sale-lease']
    PROPERTY_TYPES_COLORS.forEach(({ value, propertyType }) => {
      types.forEach((type) => {
        cases.push(
          ...[
            [
              'all',
              ['==', ['get', 'imageType'], type],
              ['==', ['get', 'primaryPropertyType'], propertyType],
              // ['in', propertyType, ['get', 'propertyTypes']],
            ],
            `${value}-${type}`,
          ]
        )
      })
    })
    return cases
  }, [])

  const unclusteredPointLayer: LayerProps = {
    id: 'property-single-clusters',
    type: 'symbol',
    filter: ['!', ['has', 'point_count']],
    layout: {
      'icon-image': [
        'case',
        ['==', ['get', 'isBusinessListing'], true],
        'orange-dark-business',
        ...layerCaseStyles,

        // default
        'red-sale-lease',
      ],
      'icon-size': ['match', ['get', 'type'], 'lease', 0.085, 0.085],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
  }

  const selectedPoint: LayerProps = {
    id: 'selected-pointer-layer',
    type: 'symbol',
    layout: {
      'icon-image': [
        'case',
        ['boolean', ['get', 'isBusinessListing'], false],
        'selected-business',

        [
          'match',
          ['get', 'type'],
          'sale',
          'selected-sale',
          'lease',
          'selected-lease',
          'sale-lease',
          'selected-sale-lease',
          'selected-sale-lease',
        ],
      ],
      'icon-size': [
        'match',
        ['get', 'type'],
        'lease',
        0.085 + 0.05,
        0.085 + 0.05,
      ],
      'icon-allow-overlap': true,
    },
  }

  if (currentMapView !== MapView.Map) {
    return null
  }

  return (
    <>
      <Source
        id="property-listings"
        type="geojson"
        data={points}
        cluster={true}
        clusterMaxZoom={CLUSTER_ZOOM_LEVEL}>
        <Layer {...clusterLayer} />
        <Layer {...clusterCountLayer} />
        <Layer {...unclusteredPointLayer} />
      </Source>
      {selectedListing && (
        <Source type="geojson" data={selectedListingPoint}>
          <Layer {...selectedPoint} />
        </Source>
      )}
    </>
  )
}
